export function parsePositiveNumber(value) {
  if (value === null || value === undefined) return null;

  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  if (typeof value === "string") {
    const cleanedValue = value.replace(/[^0-9.]/g, "");
    if (!cleanedValue) return null;

    const numericValue = Number(cleanedValue);
    return Number.isFinite(numericValue) && numericValue > 0
      ? numericValue
      : null;
  }

  return null;
}

function trackMetaPixel(event, params) {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", event, params);
  }
}

function normalizeEmail(email) {
  if (typeof email !== "string") return "";
  const normalizedEmail = email.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
    ? normalizedEmail
    : "";
}

export function identifyMetaPixelCustomer(pixelId, customer = {}) {
  const email = normalizeEmail(customer?.email);

  if (typeof window !== "undefined" && window.fbq && pixelId && email) {
    window.fbq("init", String(pixelId), { em: email });
  }
}

function getProductContentId(product, fallbackProductId) {
  return product?.sku || product?.productId || product?.id || fallbackProductId;
}

function getProductName(product, fallbackName) {
  return (
    product?.name ||
    product?.title ||
    product?.productName ||
    fallbackName ||
    "Product"
  );
}

function getProductCategory(product) {
  return (
    product?.categoryName ||
    product?.category?.categoryName ||
    product?.productCategoryName ||
    product?.category?.name ||
    null
  );
}

function getProductValue(product, selectedSize) {
  return parsePositiveNumber(
    selectedSize?.price ??
      selectedSize?.sellingPrice ??
      selectedSize?.offerPrice ??
      product?.price ??
      product?.sellingPrice ??
      product?.distributionPrice,
  );
}

function buildProductPayload(product, options = {}) {
  const contentId = getProductContentId(product, options.fallbackProductId);
  const contentCategory = getProductCategory(product);
  const value =
    parsePositiveNumber(options.value) ||
    getProductValue(product, options.selectedSize);

  return {
    content_ids: contentId ? [String(contentId)] : [],
    content_name: getProductName(product, options.fallbackName),
    content_type: "product",
    ...(contentCategory ? { content_category: contentCategory } : {}),
    ...(value ? { value } : {}),
    currency: options.currency || "INR",
  };
}

export function trackViewContent(product, options = {}) {
  if (!product) return;
  trackMetaPixel("ViewContent", buildProductPayload(product, options));
}

export function trackAddToCart(product, options = {}) {
  if (!product) return;

  const quantity = parsePositiveNumber(options.quantity) || 1;

  trackMetaPixel("AddToCart", {
    ...buildProductPayload(product, options),
    num_items: quantity,
  });
}

export function trackLead(params) {
  trackMetaPixel("Lead", params);
}

function getCheckoutItemProduct(item) {
  return item?.product || item;
}

function getCheckoutItemQuantity(item) {
  return parsePositiveNumber(item?.quantity) || 1;
}

function getCheckoutItemValue(item) {
  const product = getCheckoutItemProduct(item);

  return (
    parsePositiveNumber(item?.size?.price) ||
    parsePositiveNumber(item?.price) ||
    getProductValue(product, item?.size) ||
    0
  );
}

export function trackInitiateCheckout(items = [], options = {}) {
  const checkoutItems = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!checkoutItems.length) return;

  const contentIds = checkoutItems
    .map((item) =>
      getProductContentId(getCheckoutItemProduct(item), item?.productId),
    )
    .filter(Boolean)
    .map(String);
  const numItems = checkoutItems.reduce(
    (total, item) => total + getCheckoutItemQuantity(item),
    0,
  );
  const computedValue = checkoutItems.reduce(
    (total, item) =>
      total + getCheckoutItemValue(item) * getCheckoutItemQuantity(item),
    0,
  );
  const value = parsePositiveNumber(options.value) || computedValue;

  trackMetaPixel("InitiateCheckout", {
    content_ids: contentIds,
    content_type: "product",
    ...(value ? { value } : {}),
    currency: options.currency || "INR",
    num_items: numItems,
  });
}

export function buildPurchasePayload(items = [], options = {}) {
  const purchaseItems = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!purchaseItems.length) return null;

  const contentIds = purchaseItems
    .map((item) =>
      getProductContentId(getCheckoutItemProduct(item), item?.productId),
    )
    .filter(Boolean)
    .map(String);
  const contentNames = purchaseItems
    .map((item) => getProductName(getCheckoutItemProduct(item)))
    .filter(Boolean);
  const computedValue = purchaseItems.reduce(
    (total, item) =>
      total + getCheckoutItemValue(item) * getCheckoutItemQuantity(item),
    0,
  );
  const value = parsePositiveNumber(options.value) || computedValue;

  return {
    content_ids: contentIds,
    content_name: contentNames.join(", "),
    content_type: "product",
    ...(value ? { value } : {}),
    currency: options.currency || "INR",
  };
}

export function trackPurchase(items = [], options = {}) {
  const payload = buildPurchasePayload(items, options);
  if (!payload) return;

  trackMetaPixel("Purchase", payload);
}
